/**
 * Pure finance calculation functions extracted from useFinance hooks.
 * These are stateless helpers that can be tested without mocking Supabase.
 */

import type { BilledToInfo, InvoiceDocumentCompany } from "@/types/database";
import type { RecipientRole } from "@dallali/shared";

export type InvoiceKind = "rent" | "sale" | "management_fee" | "contract_fee" | "ejar_fee" | "subscription_fee" | "brokerage_fee" | "marketing_fee" | "property_management_fee" | "deposit" | "monthly_maintenance" | "unknown";

/**
 * Classifies an invoice by its description text and contract type.
 * Fee-specific descriptions are checked FIRST because fee invoices share
 * the parent contract's type (e.g. contract_type="rent" for a contract fee).
 */
export function classifyInvoice(
  contractType: string | undefined,
  description: string | null
): InvoiceKind {
  const d = description ?? "";

  // Check fee-specific descriptions FIRST (fees share the parent contract's type)
  // Brokerage / سعي
  if (d.includes("رسوم السعي") || d.includes("رسوم سعي") || d.includes("سعي") || /brokerage/i.test(d)) return "brokerage_fee";
  // Marketing / تسويق
  if (d.includes("رسوم التسويق") || d.includes("رسوم تسويق") || d.includes("تسويق") || /marketing/i.test(d)) return "marketing_fee";
  // Property Management / إدارة أملاك / إدارة عقار
  if (d.includes("إدارة أملاك") || d.includes("إدارة الأملاك") || d.includes("إدارة عقار") || /property\s*management/i.test(d)) return "property_management_fee";
  // Contract fee / إدارة عقد / رسوم العقد
  if (d.includes("رسوم العقد") || d.includes("إدارة عقد") || d.includes("إدارة العقود") || /contract\s*(fee|management)/i.test(d)) return "contract_fee";
  // General management fee
  if (d.includes("رسوم الإدارة") || d.includes("رسوم إدارة") || /management\s*fee/i.test(d)) return "management_fee";
  // Monthly maintenance / صيانة
  if (d.includes("صيانة شهرية") || d.includes("رسوم صيانة") || d.includes("صيانة") || /monthly\s*maintenance/i.test(d) || /maintenance\s*fee/i.test(d)) return "monthly_maintenance";
  // Deposit / تأمين
  if (d.includes("تأمين") || /deposit/i.test(d) || /security\s*deposit/i.test(d)) return "deposit";
  // Ejar fee
  if ((d.includes("رسوم") && d.includes("إيجار")) || /ejar/i.test(d)) return "ejar_fee";
  // Subscription
  if (d.includes("اشتراك") || /subscription/i.test(d)) return "subscription_fee";

  // Then fall back to contract type
  if (contractType === "rent" || contractType === "master_rent") return "rent";
  if (contractType === "sale") return "sale";
  if (contractType === "management") return "management_fee";

  // General description matching
  if (d.includes("إدارة") || /management/i.test(d)) return "management_fee";
  if (d.includes("إيجار") || /\brent\b/i.test(d)) return "rent";
  if (d.includes("بيع") || /\bsale\b/i.test(d)) return "sale";
  return "unknown";
}

/**
 * Whether an invoice of this kind is issued BY the property owner (so the
 * "Invoice From" party is the owner, not the management company / Dallali).
 *
 * Business rule: rent, sale, and the security deposit (تأمين) are the landlord's
 * money — the management company only collects on the owner's behalf — so the
 * tax document names the OWNER as issuer. Every other kind (management/brokerage/
 * marketing/property-management/contract/ejar fees, monthly maintenance, platform
 * subscription, expenses, unknown) is money Dallali itself collects, so it stays
 * issued FROM the company.
 *
 * Single source of truth — every invoice surface derives the issuer from this,
 * never inline.
 */
export function isOwnerIssuedInvoice(kind: InvoiceKind): boolean {
  return kind === "rent" || kind === "sale" || kind === "deposit";
}

/**
 * Bilingual labels for `expenses.expense_type` values. Single source of
 * truth for expense-category display — shared by ExpensesTab's table badge
 * and the Finance summary's expenses-breakdown card so the two surfaces
 * never drift apart on wording.
 */
export const EXPENSE_TYPE_LABELS: Record<string, { labelAr: string; labelEn: string }> = {
  maintenance: { labelAr: "صيانة", labelEn: "Maintenance" },
  utilities: { labelAr: "مرافق", labelEn: "Utilities" },
  insurance: { labelAr: "تأمين", labelEn: "Insurance" },
  marketing: { labelAr: "تسويق", labelEn: "Marketing" },
  management: { labelAr: "إدارة", labelEn: "Management" },
  legal: { labelAr: "قانوني", labelEn: "Legal" },
  taxes: { labelAr: "ضرائب", labelEn: "Taxes" },
  other: { labelAr: "أخرى", labelEn: "Other" },
};

/**
 * Bilingual labels for `receipts.payment_channel` values. Single source of
 * truth — shared by ReceiptDetailsModal, ReceiptDetails, and ReceiptPublicView
 * so the three render surfaces never drift apart on wording. Keys match the
 * `payment_channel` Postgres enum exactly (types.ts) — cash, bank_transfer,
 * ejar, check, card, sadad, other. (credit_card/online were dead keys the DB
 * enum never had — dropped during the 3-way consolidation.)
 */
export const RECEIPT_CHANNEL_LABELS: Record<string, { labelAr: string; labelEn: string }> = {
  cash: { labelAr: "نقداً", labelEn: "Cash" },
  bank_transfer: { labelAr: "تحويل بنكي", labelEn: "Bank Transfer" },
  ejar: { labelAr: "إيجار", labelEn: "Ejar" },
  check: { labelAr: "شيك", labelEn: "Check" },
  card: { labelAr: "بطاقة", labelEn: "Card" },
  sadad: { labelAr: "سداد", labelEn: "Sadad" },
  other: { labelAr: "أخرى", labelEn: "Other" },
};

/**
 * Bilingual labels for `receipts.transaction_type` values. Single source of
 * truth — shared by ReceiptDetailsModal and ReceiptPublicView.
 */
export const RECEIPT_TYPE_LABELS: Record<string, { labelAr: string; labelEn: string }> = {
  rent: { labelAr: "إيجار", labelEn: "Rent" },
  deposit: { labelAr: "تأمين", labelEn: "Deposit" },
  maintenance: { labelAr: "صيانة", labelEn: "Maintenance" },
  service: { labelAr: "خدمة", labelEn: "Service" },
  penalty: { labelAr: "غرامة", labelEn: "Penalty" },
  refund: { labelAr: "استرداد", labelEn: "Refund" },
  other: { labelAr: "أخرى", labelEn: "Other" },
};

/** The AddPaymentDialog payment-type option values (its PAYMENT_TYPES list). */
export type PaymentTypeOption = "rent" | "service_fee" | "maintenance" | "insurance" | "deposit" | "other";

/**
 * Default payment type for the Record Payment dialog when it opens pre-filled
 * from an invoice (context mode). The old behavior hardcoded "rent" for every
 * invoice, which mislabeled payments against expense-generated invoices (a
 * maintenance expense showed — and recorded — "rent").
 *
 * Priority: the source expense's own expense_type (the invoice was generated
 * by generate_invoice_from_expense) → classifyInvoice on the description/
 * contract type → "rent" as the legacy default for plain rent-cycle invoices.
 */
export function defaultPaymentTypeForInvoice(input: {
  sourceExpenseType?: string | null;
  contractType?: string | null;
  description?: string | null;
}): PaymentTypeOption {
  const { sourceExpenseType, contractType, description } = input;

  if (sourceExpenseType) {
    if (sourceExpenseType === "maintenance") return "maintenance";
    if (sourceExpenseType === "insurance") return "insurance";
    // electricity / water / gas / marketing / taxes / other — no dedicated
    // payment-type option; the operator can refine from "other".
    return "other";
  }

  switch (classifyInvoice(contractType ?? undefined, description ?? null)) {
    case "rent":
      return "rent";
    case "monthly_maintenance":
      return "maintenance";
    case "deposit":
      return "deposit";
    case "management_fee":
    case "contract_fee":
    case "ejar_fee":
    case "subscription_fee":
    case "brokerage_fee":
    case "marketing_fee":
    case "property_management_fee":
      return "service_fee";
    case "sale":
      return "other";
    default:
      return "rent";
  }
}

/**
 * A Saudi commercial-registration "national id" — a 10-digit number starting
 * with 70. Such owners/tenants are commercial entities whose `national_id`
 * doubles as their CR number on a tax invoice.
 */
export function isCommercialOwnerId(nationalId: string | null | undefined): boolean {
  return nationalId != null && /^70\d{8}$/.test(nationalId);
}

/**
 * Owner fields the finance helpers read. The owner table grew `owner_type` +
 * establishment columns (migration 20260623…) mirroring the tenant model, so a
 * commercial owner is now detected structurally (`owner_type === "commercial"`)
 * rather than only by the 70… `national_id` heuristic — the latter is kept as a
 * fallback for legacy rows whose `owner_type` was never backfilled.
 */
type OwnerNameFields = {
  full_name: string;
  full_name_en?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  national_id?: string | null;
  // Loose DB text (CHECK in {'individual','commercial'}) so the Supabase
  // generated row + OwnerFragment assign cleanly; narrowed by an `=== "commercial"`
  // comparison below.
  owner_type?: string | null;
  commercial_name?: string | null;
  commercial_name_en?: string | null;
  representative_name?: string | null;
  // Present for parity with OwnerFragment / the DB model. The resolver never
  // falls back to an English representative name (the rep is never the displayed
  // owner), so this has no behaviour impact — it only keeps the type complete.
  representative_name_en?: string | null;
  cr_number?: string | null;
  unified_number?: string | null;
  // BUYER-side VAT registration number (distinct from the ZATCA seller VAT —
  // see ownerToInvoiceFrom). Surfaced on the "Billed To" block via
  // ownerToBilledTo below.
  vat_number?: string | null;
};

/**
 * True when an owner is a commercial establishment: structurally via
 * `owner_type`, or — for legacy rows with no `owner_type` — via the 70…
 * `national_id` CR-number heuristic.
 */
function isCommercialOwner(owner: Pick<OwnerNameFields, "owner_type" | "national_id">): boolean {
  return owner.owner_type === "commercial" || isCommercialOwnerId(owner.national_id);
}

/**
 * Resolve the displayed owner NAME. For a commercial owner the establishment
 * name is shown (never the representative, who is a separate contact only):
 * ar → commercial_name → full_name → representative_name; en →
 * commercial_name_en → commercial_name → full_name_en → full_name. Individual
 * owners are unchanged — full_name (en prefers full_name_en).
 */
function resolveOwnerName(owner: OwnerNameFields, language: string): string {
  if (isCommercialOwner(owner)) {
    if (language === "ar") {
      return owner.commercial_name || owner.full_name || owner.representative_name || "";
    }
    return owner.commercial_name_en || owner.commercial_name || owner.full_name_en || owner.full_name || "";
  }
  return language === "ar" ? owner.full_name : (owner.full_name_en || owner.full_name);
}

/**
 * Build BilledToInfo from owner data.
 */
function ownerToBilledTo(
  owner: OwnerNameFields,
  language: string
): BilledToInfo {
  const commercial = isCommercialOwner(owner);
  return {
    name: resolveOwnerName(owner, language),
    // Commercial → establishment EN name (never the representative's), matching
    // `ownerToInvoiceFrom`. Individual → unchanged. InvoiceDocument renders nameEn
    // as a secondary line when it differs from name, so for a commercial owner it
    // must be the establishment, not full_name_en (the rep's EN name).
    nameEn: commercial ? (resolveOwnerName(owner, "en") || null) : (owner.full_name_en ?? null),
    phone: owner.phone ?? null,
    email: owner.email ?? null,
    address: owner.address ?? null,
    type: "owner",
    entityType: commercial ? "commercial" : "individual",
    // ZATCA: commercial → CR only; individual → national id only. A commercial
    // owner whose national_id is a 70… CR must NOT also emit a national-id row.
    crNumber: owner.cr_number ?? (isCommercialOwnerId(owner.national_id) ? owner.national_id! : null),
    // Buyer VAT — only meaningful for a commercial owner (individuals have no
    // VAT registration). Distinct from the seller-of-record VAT in
    // ownerToInvoiceFrom, which is deliberately left null (separate, gated change).
    vatNumber: commercial ? (owner.vat_number ?? null) : null,
    idNumber: commercial ? null : (owner.national_id ?? null),
    idType: !commercial && owner.national_id ? "national_id" : null,
  };
}

/**
 * Map a property owner to the `InvoiceDocumentCompany` shape rendered in the
 * "الفاتورة من / Invoice From" block, for owner-issued invoices (see
 * {@link isOwnerIssuedInvoice}). Both the Arabic and English names are passed
 * through so the block shows them exactly like the company header. A commercial
 * owner's `national_id` (70…) surfaces as the CR number. Owner VAT is stored on
 * the owner record (migration 20260623120000) but NOT yet surfaced here, so
 * `vat_number` is left null — wiring the owner's VAT as the ZATCA seller for
 * commercial rent is the separate, gated seller-of-record change; until then the
 * ZATCA QR seller stays the management company.
 *
 * Returns null when there is no owner record, so callers fall back to the
 * company without special-casing.
 *
 * Commercial owners surface the ESTABLISHMENT name (and Arabic display) here too
 * — the representative is never the named issuer. Both the Arabic name and the
 * English name follow the same establishment-first rule as {@link ownerToBilledTo}.
 */
export function ownerToInvoiceFrom(
  owner: OwnerNameFields | null | undefined,
): InvoiceDocumentCompany | null {
  if (!owner) return null;
  const commercial = isCommercialOwner(owner);
  return {
    // Commercial → establishment name (ar + en, rep never wins). Individual →
    // unchanged: the raw full_name and full_name_en (null when absent), so the
    // "الفاتورة من" block matches the company header exactly as before.
    name: commercial ? resolveOwnerName(owner, "ar") : owner.full_name,
    name_en: commercial ? (resolveOwnerName(owner, "en") || null) : (owner.full_name_en ?? null),
    cr_number: owner.cr_number ?? (isCommercialOwnerId(owner.national_id) ? owner.national_id! : null),
    vat_number: null,
    address: owner.address ?? null,
  };
}

/**
 * Build BilledToInfo from tenant data. Exported (in addition to being used
 * internally by buildBilledToInfo) so the master-invoice builders
 * (buildMasterInvoiceDoc.tsx, MasterInvoicesTab.tsx) can resolve a TENANT
 * buyer party the SAME way regular invoices do — a commercial tenant's CR/VAT
 * must never be reimplemented ad hoc (issue #3096).
 */
export function tenantToBilledTo(
  tenant: { tenant_type?: string | null; commercial_name?: string | null; commercial_name_en?: string | null; first_name?: string | null; last_name?: string | null; first_name_en?: string | null; last_name_en?: string | null; phone?: string | null; email?: string | null; address?: string | null; cr_number?: string | null; vat_number?: string | null; id_number?: string | null; id_type?: string | null },
  language: string
): BilledToInfo {
  const isCommercial = tenant.tenant_type === "commercial";
  let fullName = "";
  let fullNameEn = "";
  if (isCommercial) {
    fullName = tenant.commercial_name || tenant.commercial_name_en || "";
    fullNameEn = tenant.commercial_name_en || tenant.commercial_name || "";
  } else {
    fullName = `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim();
    fullNameEn = `${tenant.first_name_en || tenant.first_name || ""} ${tenant.last_name_en || tenant.last_name || ""}`.trim();
  }
  return {
    name: (language === "ar" ? fullName : fullNameEn) || fullName || "---",
    nameEn: fullNameEn || null,
    phone: tenant.phone ?? null,
    email: tenant.email ?? null,
    address: tenant.address ?? null,
    type: "tenant",
    entityType: isCommercial ? "commercial" : "individual",
    crNumber: tenant.cr_number ?? null,
    // Buyer VAT — only meaningful for a commercial tenant (individuals have no
    // VAT registration). Mirrors ownerToBilledTo's commercial-only guard.
    vatNumber: isCommercial ? (tenant.vat_number ?? null) : null,
    idNumber: tenant.id_number ?? null,
    idType: tenant.id_type ?? null,
  };
}

/**
 * The structural billed-party flag stored on `invoices.recipient_role`. The
 * canonical type lives in the FE↔Nest contract (`@dallali/shared`, pinned to the
 * DB CHECK); re-exported here so existing finance call-sites keep importing it
 * from this helper.
 */
export type { RecipientRole };

const RECIPIENT_ROLES: readonly RecipientRole[] = ["owner", "tenant", "third_party"];

/**
 * Narrow a raw `recipient_role` value (the DB column is `text | null`) to the
 * typed {@link RecipientRole} union, returning `null` for absent/unknown values.
 * Use at every call site that feeds a DB-sourced role into buildBilledToInfo /
 * resolveBilledToType so the param stays strictly typed without unsafe casts.
 */
export function asRecipientRole(value: string | null | undefined): RecipientRole | null {
  return value != null && (RECIPIENT_ROLES as readonly string[]).includes(value)
    ? (value as RecipientRole)
    : null;
}

/**
 * Derive BilledToInfo from invoice/receipt data.
 * Shared by InvoiceDetails, InvoiceDetailsModal, and ReceiptDetailsModal.
 */
export function buildBilledToInfo(params: {
  ownerIdOnContract: string | undefined;
  billedToId: string | undefined;
  owner: Parameters<typeof ownerToBilledTo>[0] | null | undefined;
  tenant: Parameters<typeof tenantToBilledTo>[0] | null | undefined;
  description?: string | null;
  language: string;
  /**
   * Structural billed-party flag (invoices.recipient_role) — the AUTHORITATIVE
   * signal when present. Expense-generated invoices (and contract invoices) set
   * this, so owner-billed expenses no longer fall through to the tenant default
   * just because their description has no مالك/owner marker (ticket 86exwj1jg).
   * Legacy rows have it NULL → fall back to the description/billedToId heuristics.
   */
  recipientRole?: RecipientRole | null;
}): BilledToInfo | null {
  const { ownerIdOnContract, billedToId, owner, tenant, description, language, recipientRole } = params;
  const desc = (description || "").toLowerCase();

  // Authoritative structural signal wins over every heuristic below.
  if (recipientRole === "owner") return owner ? ownerToBilledTo(owner, language) : null;
  if (recipientRole === "tenant") return tenant ? tenantToBilledTo(tenant, language) : null;
  // 'third_party' has no owner/tenant record to resolve — return null (no billed
  // party shown) rather than misattributing to the tenant via the heuristics below.
  if (recipientRole === "third_party") return null;

  // Fee invoices: payer is encoded in description (مالك/Owner vs مستأجر/Tenant).
  // Check this FIRST — fee invoices have no tenant_id but the contract join
  // still provides tenant data, so we must check description before the tenant fallback.
  const descHasOwner = desc.includes("owner") || desc.includes("seller") || desc.includes("بائع") || desc.includes("\u0645\u0627\u0644\u0643");
  const descHasTenant = desc.includes("tenant") || desc.includes("buyer") || desc.includes("مشتري") || desc.includes("\u0645\u0633\u062a\u0623\u062c\u0631");
  // Fee invoices encode payer in description — respect it strictly.
  // Never fall through to the wrong party (e.g. showing tenant for owner-paid fees).
  if (descHasOwner) return owner ? ownerToBilledTo(owner, language) : null;
  if (descHasTenant) return tenant ? tenantToBilledTo(tenant, language) : null;

  // Standard invoices: billedToId matches owner_id on contract → owner-billed
  const isOwnerBilled = ownerIdOnContract && billedToId === ownerIdOnContract;
  if (isOwnerBilled && owner) return ownerToBilledTo(owner, language);

  // Default: bill to tenant
  if (tenant) return tenantToBilledTo(tenant, language);

  return null;
}

/**
 * Generate ZATCA-compliant TLV (Tag-Length-Value) encoded QR data string.
 */
export function generateZatcaTlv(data: {
  sellerName: string;
  vatNumber: string;
  invoiceDate: string;
  totalAmount: number;
  vatAmount: number;
}): string {
  const encoder = new TextEncoder();
  const tlvData: Uint8Array[] = [];

  const sellerBytes = encoder.encode(data.sellerName);
  tlvData.push(new Uint8Array([1, sellerBytes.length, ...sellerBytes]));

  const vatBytes = encoder.encode(data.vatNumber || "");
  tlvData.push(new Uint8Array([2, vatBytes.length, ...vatBytes]));

  const dateBytes = encoder.encode(data.invoiceDate);
  tlvData.push(new Uint8Array([3, dateBytes.length, ...dateBytes]));

  const totalBytes = encoder.encode(data.totalAmount.toFixed(2));
  tlvData.push(new Uint8Array([4, totalBytes.length, ...totalBytes]));

  const vatAmountBytes = encoder.encode(data.vatAmount.toFixed(2));
  tlvData.push(new Uint8Array([5, vatAmountBytes.length, ...vatAmountBytes]));

  const combined = new Uint8Array(tlvData.reduce((acc, arr) => acc + arr.length, 0));
  let offset = 0;
  for (const arr of tlvData) {
    combined.set(arr, offset);
    offset += arr.length;
  }

  return btoa(String.fromCharCode(...combined));
}

/**
 * Calculates management fees based on fee type.
 * - "percentage": percentage of total receipts
 * - otherwise: flat fee per receipt
 */
export function calculateManagementFees(
  feeType: "percentage" | string,
  feeValue: number,
  totalReceipts: number,
  receiptCount: number
): number {
  if (feeType === "percentage") {
    return Math.round(totalReceipts * (feeValue / 100));
  }
  return receiptCount * feeValue;
}

/**
 * Calculates occupancy rate as a rounded percentage.
 * Returns 0 if total is 0 (division-by-zero guard).
 */
export function calculateOccupancyRate(occupied: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((occupied / total) * 100);
}

// Saudi VAT standard rate. Used as a floor when companies.vat_rate is null.
// Fee services are commercial regardless of lease type, so VAT applies.
export const DEFAULT_VAT_RATE_PERCENT = 15;

/**
 * Converts a net SAR amount (e.g. a contract fee column) to gross by applying
 * the company's VAT rate. Receipts pay invoices at gross — Total must be gross
 * too or the Collected/Total ratio renders > 100% for any paid fee.
 */
export function applyVat(net: number, vatRatePercent: number | null | undefined): number {
  const rate = vatRatePercent ?? DEFAULT_VAT_RATE_PERCENT;
  return net * (1 + rate / 100);
}

/**
 * Sums `total_amount` (already gross) of fee invoices that don't link to a
 * contract — monthly aggregate bills, one-off campaigns, owner-billed
 * portfolio fees. Per-contract Total reads contracts.{col}, so without this
 * the standalone invoices vanish from Total while their receipts still count
 * in Collected, producing a misleading "Collected > Total".
 *
 * Pure function — caller pre-classifies invoices and passes the kind→id map.
 * Keeps the hook thin and the math testable in isolation.
 */
export function sumStandaloneFeeGross(
  invoices: { id: string; contract_id: string | null; total_amount: number | null }[],
  invoiceKindById: Map<string, InvoiceKind>,
  kind: InvoiceKind,
): number {
  return invoices
    .filter((i) => i.contract_id == null && invoiceKindById.get(i.id) === kind)
    .reduce((acc, i) => acc + (i.total_amount ?? 0), 0);
}

/**
 * Computes the invoice's status after its paid_amount changes (e.g. after a
 * receipt is deleted or added). Pure function — caller passes the new
 * paid_amount, the invoice total, due_date, and current status.
 *
 *   paid >= total (and total > 0)         → "paid"
 *   0 < paid < total                      → "partial"
 *   paid === 0 && due_date < reference    → "overdue"
 *   paid === 0 && otherwise               → "issued"
 *
 * Cancelled invoices are returned unchanged — that status is a manual
 * override and shouldn't flip just because a receipt was reconciled.
 *
 * The `now` argument is optional and only used by tests to pin a reference
 * time when asserting overdue vs issued.
 */
export type InvoiceStatusAfterReconcile = "paid" | "partial" | "issued" | "overdue" | "cancelled";

export function computeInvoiceStatusAfterPaidAmountChange(args: {
  newPaidAmount: number;
  totalAmount: number;
  dueDate: string | null | undefined;
  currentStatus?: string | null;
  now?: Date;
}): InvoiceStatusAfterReconcile {
  if (args.currentStatus === "cancelled") return "cancelled";
  const total = args.totalAmount ?? 0;
  if (args.newPaidAmount >= total && total > 0) return "paid";
  if (args.newPaidAmount > 0) return "partial";
  const due = args.dueDate ? new Date(args.dueDate) : null;
  const reference = args.now ?? new Date();
  return due && due.getTime() < reference.getTime() ? "overdue" : "issued";
}

// ── Service line-item lumping ───────────────────────────────────────────────

/**
 * Descriptions the contract RPCs emit for per-unit service line items, e.g.
 * "صرف صحي / Sewage — وحدة / Unit 9". Per the Ejar model the CEO referenced,
 * all of these collapse into ONE "الخدمات / Services" line shown after rent —
 * and services are VAT-exempt (the VAT sits entirely on the rent line). The
 * already-lumped "الخدمات / Services" label is matched too so re-lumping is a
 * safe no-op.
 *
 * KNOWN CONSTRAINT: `/\bservices\b/i` is intentionally broad and would also
 * match a hypothetical future line whose English text contains "services"
 * (e.g. a "Utility services levy"). This is acceptable under Dallali's strict
 * bilingual "<ar> / <en>" description convention (only the contract RPCs emit
 * these per-unit service lines, and fee invoices carry no line items), but a
 * new line-item description that uses the word "services" must NOT rely on
 * being shown separately on a rent invoice.
 */
const SERVICE_LINE_ITEM_PATTERNS: readonly RegExp[] = [
  /صرف صحي/, /\bsewage\b/i,
  /صيانة/, /\bmaintenance\b/i,
  /نظافة/, /\bcleaning\b/i,
  /أمن/, /\bsecurity\b/i,
  /كهرباء/, /\belectricity\b/i,
  /ماء/, /\bwater\b/i,
  /غاز/, /\bgas\b/i,
  /إنترنت/, /\binternet\b/i,
  /الخدمات/, /\bservices\b/i,
];

/** Bilingual label for the single merged services line ("ar / en" convention). */
export const LUMPED_SERVICES_LABEL = "الخدمات / Services";

/** True when an invoice line-item description is one of the contract services. */
export function isServiceLineItem(description: string | null | undefined): boolean {
  const d = description ?? "";
  if (!d) return false;
  return SERVICE_LINE_ITEM_PATTERNS.some((re) => re.test(d));
}

/** Minimal raw invoice-item shape the lumper reads (DB `invoice_items` row). */
export interface LumpableLineItem {
  id?: string | null;
  description: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  total?: number | null;
}

/**
 * Collapse the per-service line items of a contract invoice into a SINGLE
 * "الخدمات / Services" row, keeping rent (and any non-service rows) untouched
 * and in their original order. The merged row is placed where the first
 * service row was, so it renders directly after the rent line — matching the
 * Ejar layout the CEO asked for.
 *
 * Done at the display layer (not the DB) so it also fixes already-generated
 * invoices without requiring a re-save, and so every invoice surface (modal,
 * finance detail, public view, print) shows one services line consistently.
 *
 * `unit_price`/`total` are summed across the merged services; `quantity` is 1.
 * Invoices with zero or one service row are returned unchanged.
 */
export function lumpServiceLineItems<T extends LumpableLineItem>(
  items: ReadonlyArray<T>,
): T[] {
  const serviceCount = items.filter((it) => isServiceLineItem(it.description)).length;
  if (serviceCount <= 1) return [...items];

  let unitPriceSum = 0;
  let totalSum = 0;
  for (const it of items) {
    if (isServiceLineItem(it.description)) {
      unitPriceSum += Number(it.unit_price) || 0;
      totalSum += Number(it.total) || 0;
    }
  }

  let merged = false;
  const result: T[] = [];
  for (const it of items) {
    if (!isServiceLineItem(it.description)) {
      result.push(it);
      continue;
    }
    if (merged) continue; // drop the remaining service rows
    merged = true;
    // Display-only rounding to 2dp. Each per-service amount is already
    // ROUND(_, 2) at the DB (the RPCs round per service before storing), so
    // summing them here is exact for the typical case; the round just guards
    // against float accumulation when many rows are merged. This figure is for
    // display and never written back, so it can't drift the stored invoice total.
    result.push({
      ...it,
      id: it.id ?? "services-lumped",
      description: LUMPED_SERVICES_LABEL,
      quantity: 1,
      unit_price: Math.round(unitPriceSum * 100) / 100,
      total: Math.round(totalSum * 100) / 100,
    });
  }
  return result;
}
